/**
 * Announcements are messages sent to all projects by an SLTT root user.
 * These are most commonly to inform users of new features or changes.
 * They are a special type of ProjectMessage, i.e. isAnnouncement = true.
 * If there are any unviewed announcements, they are shown in a modal dialog when the user opens the app.
 * The user can choose to not show a specific announcement again.
 * If the app is already open and a new announcement is sent, the user will be notified with a toast
 * and given the option to view the announcement(s).
 */

import { FC, useEffect, useState } from 'react'
import { t } from 'ttag'
import { observer } from 'mobx-react'
import _ from 'underscore'
import Modal from 'react-bootstrap/lib/Modal'
import { Id, toast } from 'react-toastify'
import _debug from 'debug'

import { Root } from '../../models3/Root'
import { displayError } from '../utils/Errors'
import { ProjectMessage } from '../../models3/ProjectModels'
import './ProjectMessages.css'
import './Announcements.css' // Import the CSS file
import { _LevelupDB } from '../../models3/_LevelupDB'
import { PreviousSegmentButton, NextSegmentButton, PaneCloseButton, DontShowAgainButtonWithLabel } from '../utils/Buttons'
import { RichTextViewer } from '../utils/RichTextEditor'
import Player from '../utils/Player'
import { AnnouncementIcon } from '../utils/Icons'
import { fmt } from '../utils/Fmt'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { AppRoot } from '../../models3/AppRoot'

const log = _debug('sltt:ProjectAnnouncements')


interface IShowAnnouncement {
    message: ProjectMessage | null,
}

const ShowAnnouncement: FC<IShowAnnouncement> = ({ message }) => {
    if (!message) { return null }

    const { text, videoUrl } = message

    const prefix = 'announcement'

    function onEnded() { }

    const dontShow = t`Don't show this announcement again`

    return (
        <div className="announcement">
            <div className="announcement-content">
                <div className="announcement-left">
                    {videoUrl &&
                        <Player {...{ className: 'announcement', videoUrl, onEnded, hideCloseButton: true }} />}
                </div>
                <div className="announcement-right">
                    {text && <RichTextViewer {...{ text, prefix }} />}
                </div>
            </div>
            <div>
                <DontShowAgainButtonWithLabel
                    label={dontShow}
                    tooltip={dontShow}
                    enabled={true}
                    onClick={() => message.setViewed(true).catch(displayError)} />
            </div>
        </div>
    )
}

interface IAnnouncementHeader {
    itemNumber: number,
    announcements: ProjectMessage[],
    goToPreviousItem: () => void,
    goToNextItem: () => void,
    closeDialog: () => void,
}

const AnnouncementHeader: FC<IAnnouncementHeader> = observer((props) => {
    const { itemNumber, announcements, goToPreviousItem, goToNextItem, closeDialog } = props
    const itemCount = announcements.length
    const multipleItems = itemCount > 1
    const selection = multipleItems ? `(${itemNumber + 1} / ${itemCount})` : ''

    const announcementSelectionHeader = (
        <>
            <div className="announcement-navigator">
                <AnnouncementIcon className="announcement-icon" tooltip="" />

                <span className="announcement-subject">
                    {announcements[itemNumber]?.subject || '***' /* avoid crash if user hides last announcement */}
                    <span className="announcement-selection">{selection}</span>
                </span>
            </div>
        </>)

    return (
        <div className="announcement-header">
            <div className="announcement-header-left">
            <div className="announcement-navigator">
                {multipleItems && <PreviousSegmentButton
                    onClick={() => goToPreviousItem()}
                    tooltip={/* translator: important */ t`Go to previous announcement.`}
                    enabled={itemNumber > 0} />}

                {multipleItems && <span>
                    <NextSegmentButton
                        onClick={() => goToNextItem()}
                        tooltip={/* translator: important */ t`Go to next announcement.`}
                        enabled={itemNumber < itemCount - 1} />
                </span>}
            </div>
            </div>
            <div className="announcement-header-center">
                {announcementSelectionHeader}
            </div>
            <div className="announcement-header-right">
                <PaneCloseButton
                    enabled={true}
                    onClick={closeDialog}
                    tooltip={/* translator: important */ t`Close`}
                    className="note-dialog-close-button" />
            </div>
        </div>
    )
})

interface IProjectAnnouncements {
    announcementsRt: Root,
    dialogClosed?: () => void,
}

/** A modal dialog showing all the announcements that have not yet been marked as 'viewed' */
const ProjectAnnouncements: FC<IProjectAnnouncements> = observer(({ announcementsRt, dialogClosed }) => {
    let { project } = announcementsRt
    let announcements = _.sortBy(project.viewableAnnouncements, n => n.creationDate).reverse()

    let [currentIndex, setCurrentIndex] = useState(0) // announcement index being shown
    let [showModal, setShowModal] = useState(true)

    // This does NOT appear to reliably run when dialog is closed.
    // useEffect(() => {
    //     // Cleanup function runs when the component unmounts
    //     return () => {
    //         log('ProjectAnnouncements unmounted')
    //         dialogClosed && dialogClosed()
    //     }
    // }, [])

    useEffect(() => {
        // try to avoid crash when user clicks `Don't show this announcement again`
        // at the end of the announcements list when there is more than one announcement
        if (announcements.length === 0) {
            log('ProjectAnnouncements no announcements')
            setShowModal(false)
            return
        }
        if (announcements.length < currentIndex + 1) {
            setCurrentIndex(announcements.length - 1)
        }
    }, [announcements, currentIndex])

    if (announcements.length === 0) { return null }

    function goToPreviousItem() {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1)
        }
    }

    function goToNextItem() {
        if (currentIndex < announcements.length - 1) {
            setCurrentIndex(currentIndex + 1)
        }
    }

    function closeDialog() {
        log('ProjectAnnouncements closeDialog')
        dialogClosed && dialogClosed()
        setShowModal(false)
    }

    const announcement = announcements[currentIndex]

    return (
        <Modal show={showModal} onHide={closeDialog} bsSize="large">
            <Modal.Header>
                <AnnouncementHeader
                    announcements={announcements}
                    itemNumber={currentIndex}
                    goToPreviousItem={goToPreviousItem}
                    goToNextItem={goToNextItem}
                    closeDialog={closeDialog}
                />
            </Modal.Header>
            <Modal.Body>
                <ShowAnnouncement message={announcement || null} />
            </Modal.Body>
        </Modal>
    )
})

interface IShowAnnouncementsToast {
    closeToast?: () => void,
    showAnnouncementsDialog: () => void,
}

const ShowAnnouncementsToast: React.FC<IShowAnnouncementsToast> = ({ closeToast, showAnnouncementsDialog }) => (
    <div className="show-announcements-toast">
        <button onClick={() => {
            showAnnouncementsDialog()
            closeToast && closeToast()
        }}>
            <AnnouncementIcon className="announcement-icon" tooltip="" />
            <span className="show-announcements-toast-text">{t`Show announcements?`}</span>
        </button>
    </div>
)

/** If an announcement arrives after startup, display a toast asking the user if they want to view announcements.
 * At startup automatically show the announcements dialog if there are any announcements.
 */
function useAnnouncementToast(
    appRoot: AppRoot,
    announcementsRt: Root|null,
    announcements: ProjectMessage[],
    setAnnouncements: (announcements: ProjectMessage[]) => void) {
    const [toastId, setToastId] = useState<Id>('')
    const [firstRender, setFirstRender] = useState(true)

    const _announcements = announcementsRt?.project.
        viewableAnnouncements.
        filter(a => !a.shownInDialog) || []

    const { initialized } = announcementsRt?.project || { initialized: false } // True after all docs have been accepted

    function showAnnouncementsDialog() {
        log('showAnnouncementsDialog')
        setAnnouncements(_announcements)
        setFirstRender(false)
    }

    const flags = useFlags()

    useEffect(() => {
        if (!flags.userAnnouncements) { return }

        if (!announcementsRt) {
            appRoot.initializeAnnouncements()
            return
        }
        
        if (!initialized) { return } // wait until all docs are accepted

        // If no announcements, set firstRender to false
        // to cause user to see a toast when a new announcement is accepted (rather than being
        // immediately shown the dialog, like we do if there is an announcement at start up).
        if (_announcements.length === 0 && firstRender) {
            log('no announcements at startup')
            setFirstRender(false)
            return
        }

        if (_announcements.length === 0) { return } // nothing to see
        if (announcements.length > 0) {
            // already showing dialog
            // inhibit the dialog/toast from being shown again until there
            // are new announcements.
            _announcements.forEach(a => a.shownInDialog = true)
            return
        }
        if (toastId !== '') { return } // already showing toast

        log('useAnnouncementToast', fmt({
            announcements: _announcements.length,
            firstRender,
            toastId,
        }))

        // There were announcements available at startup, show them immediately
        if (firstRender) {
            showAnnouncementsDialog()
            return
        }

        // New announcements have been arrived.
        // Show a toast to let the user know
        const newToastId = toast(<ShowAnnouncementsToast
            showAnnouncementsDialog={showAnnouncementsDialog} />,
            {
                onClose: () => {
                    log('close toast')
                    setToastId('')
                },
                autoClose: 30000,
                className: 'announcements-toast'
            })
        setToastId(newToastId)
    }, [announcementsRt, _announcements, initialized])
}

export const ShowAnnouncements: FC<{ appRoot: AppRoot, announcementsRt: Root|null }> = observer(({ appRoot, announcementsRt }) => {
    const [announcements, setAnnouncements] = useState<ProjectMessage[]>([])

    useAnnouncementToast(appRoot, announcementsRt, announcements, setAnnouncements)

    if (!announcementsRt || announcements.length === 0) { return null }

    return (
        <>
            <ProjectAnnouncements 
                announcementsRt={announcementsRt}
                dialogClosed={() => { setAnnouncements([]) }} />
        </>
    )
})

export default ProjectAnnouncements
